import { ESPLoader, Transport } from 'esptool-js';

export class FuturonESPLoader {
  constructor() {
    this.port = null;
    this.transport = null;
    this.esploader = null;
    this.chipInfo = null;
    this.connected = false;

    this.onLog = null; // function(msg, type)
    this.onProgress = null; // function(written, total, percentage, speedKbps)
    this.onStatusChange = null; // function(connected, chipInfo)
  }

  static isSupported() {
    return 'serial' in navigator;
  }

  log(message, type = 'info') {
    console.log(`[ESPLoader:${type}]`, message);
    if (this.onLog) {
      this.onLog(message, type);
    }
  }

  async connect(baudRate = 921600, chipFamily = 'auto', customPort = null) {
    if (!FuturonESPLoader.isSupported()) {
      throw new Error('Web Serial API is not supported in this browser.');
    }

    try {
      if (customPort) {
        this.port = customPort;
      } else {
        this.log('Requesting serial port selection from user dialog...', 'info');
        this.port = await navigator.serial.requestPort();
      }
      
      this.transport = new Transport(this.port);
      this.log('Opening serial port...', 'info');
      
      // Initialize ESPLoader
      const terminalObj = {
        clean: () => {},
        writeLine: (data) => this.log(data, 'info'),
        write: (data) => this.log(data, 'info')
      };

      this.esploader = new ESPLoader({
        transport: this.transport,
        baudrate: parseInt(baudRate, 10),
        terminal: terminalObj,
        debugLogging: false
      });

      this.log('Connecting to ROM Bootloader...', 'info');
      const chip = await this.esploader.main();
      
      // Read chip information
      const mac = await this.esploader.chip.readMac(this.esploader);
      const flashSize = await this.esploader.getFlashSize();

      this.chipInfo = {
        chipName: chip || 'ESP32',
        mac: mac ? mac.toUpperCase() : 'UNKNOWN',
        flashSize: flashSize || '4MB',
        crystalFreq: this.esploader.chip.CRYSTAL_FREQ || '40MHz',
        revision: this.esploader.chip.REVISION || '1.0'
      };

      this.connected = true;
      this.log(`Successfully connected to ${this.chipInfo.chipName} (MAC: ${this.chipInfo.mac})`, 'success');

      if (this.onStatusChange) {
        this.onStatusChange(true, this.chipInfo);
      }

      return this.chipInfo;
    } catch (err) {
      this.log(`Failed to connect: ${err.message}`, 'error');
      await this.disconnect();
      throw err;
    }
  }

  async disconnect() {
    try {
      if (this.transport) {
        await this.transport.disconnect();
      }
    } catch (e) {
      console.warn('Error during disconnect transport:', e);
    } finally {
      this.port = null;
      this.transport = null;
      this.esploader = null;
      this.chipInfo = null;
      this.connected = false;

      if (this.onStatusChange) {
        this.onStatusChange(false, null);
      }
      this.log('Serial port disconnected.', 'info');
    }
  }

  async eraseFlash() {
    if (!this.connected || !this.esploader) {
      throw new Error('No target device connected.');
    }

    try {
      this.log('Erasing flash memory (this may take 10-20 seconds)...', 'warning');
      await this.esploader.eraseFlash();
      this.log('Flash erased successfully!', 'success');
    } catch (err) {
      this.log(`Flash erase failed: ${err.message}`, 'error');
      throw err;
    }
  }

  async writeFlash(fileArray, eraseBeforeWrite = true) {
    if (!this.connected || !this.esploader) {
      throw new Error('No target device connected.');
    }

    if (!fileArray || fileArray.length === 0) {
      throw new Error('No binary files provided to flash.');
    }

    try {
      if (eraseBeforeWrite) {
        await this.eraseFlash();
      }

      this.log(`Starting flashing ${fileArray.length} binary file(s)...`, 'info');
      const startTime = Date.now();

      // Convert Uint8Array/ArrayBuffer binary data into binary string expected by esptool-js
      const formattedFiles = fileArray.map(file => {
        let dataStr = file.data;
        if (typeof dataStr !== 'string') {
          const u8 = dataStr instanceof Uint8Array ? dataStr : new Uint8Array(dataStr);
          const chunkSize = 0x8000;
          let bStr = '';
          for (let i = 0; i < u8.length; i += chunkSize) {
            bStr += String.fromCharCode.apply(null, u8.subarray(i, i + chunkSize));
          }
          dataStr = bStr;
        }
        return {
          data: dataStr,
          address: file.address
        };
      });

      const fileOptions = {
        fileArray: formattedFiles,
        flashSize: 'keep',
        flashMode: 'keep',
        flashFreq: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (fileIndex, written, total) => {
          const elapsedTime = (Date.now() - startTime) / 1000; // seconds
          const speedKbps = elapsedTime > 0 ? ((written / 1024) / elapsedTime).toFixed(1) : 0;
          const percentage = Math.round((written / total) * 100);

          if (this.onProgress) {
            this.onProgress(written, total, percentage, speedKbps);
          }
        }
      };

      await this.esploader.writeFlash(fileOptions);

      this.log('Firmware flashing completed successfully!', 'success');
      
      // Hardware reset to run application
      const devName = (this.chipInfo && this.chipInfo.chipName) || 'device';
      this.log(`Resetting ${devName} to start application firmware...`, 'info');
      await this.resetDevice();

    } catch (err) {
      this.log(`Flashing failed: ${err.message}`, 'error');
      throw err;
    }
  }

  async resetDevice() {
    if (this.transport) {
      try {
        await this.transport.setDTR(false);
        await this.transport.setRTS(true);
        await new Promise(r => setTimeout(r, 100));
        await this.transport.setRTS(false);
        this.log('Hardware reset executed.', 'info');
      } catch (e) {
        this.log(`Reset failed: ${e.message}`, 'warning');
      }
    }
  }
}
